import React, { useState, useEffect, useRef } from 'react';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../context/AuthContext';
import OptimizedImage from '../components/OptimizedImage';
import { Search, Paperclip, Send } from 'lucide-react';
import * as api from '../lib/api';

const MOCK_CONVERSATIONS = [
  { conversation_id: 'conv_1', company_name: 'TechCorp AI', company_logo: 'https://ui-avatars.com/api/?name=TechCorp+AI&background=6366F1&color=fff', last_message: 'Thanks for your interest! When can you start?', unread_count: 2, last_message_at: new Date().toISOString() },
  { conversation_id: 'conv_2', company_name: 'DataMinds', company_logo: 'https://ui-avatars.com/api/?name=DataMinds&background=8B5CF6&color=fff', last_message: 'Could you share your portfolio?', unread_count: 0, last_message_at: new Date().toISOString() },
  { conversation_id: 'conv_3', company_name: 'AI Research Lab', company_logo: 'https://ui-avatars.com/api/?name=AI+Research+Lab&background=10B981&color=fff', last_message: 'Your profile looks impressive!', unread_count: 0, last_message_at: new Date().toISOString() },
];

const MOCK_MESSAGES = {
  conv_1: [
    { message_id: 'm1', sender_id: 'company', text: 'Hi! We reviewed your application for Senior AI Engineer.', created_at: new Date().toISOString() },
    { message_id: 'm2', sender_id: 'me', text: "Thank you! I'm very excited about this opportunity.", created_at: new Date().toISOString() },
    { message_id: 'm3', sender_id: 'company', text: 'Thanks for your interest! When can you start?', created_at: new Date().toISOString() },
  ],
  conv_2: [
    { message_id: 'm4', sender_id: 'company', text: 'Could you share your portfolio?', created_at: new Date().toISOString() },
  ],
  conv_3: [
    { message_id: 'm5', sender_id: 'company', text: 'Your profile looks impressive!', created_at: new Date().toISOString() },
  ],
};

const MessagesPage = () => {
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [selectedConv, setSelectedConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [useApi, setUseApi] = useState(true);
  const messagesEndRef = useRef(null);

  useEffect(() => { loadConversations(); }, [user]);

  const loadConversations = async () => {
    try {
      const data = await api.getConversations();
      setConversations(data.conversations || []);
    } catch {
      setConversations(MOCK_CONVERSATIONS);
      setUseApi(false);
    }
  };

  const selectConversation = async (conv) => {
    setSelectedConv(conv);
    try {
      if (useApi) {
        const data = await api.getMessages(conv.conversation_id);
        setMessages(data.messages || []);
      } else {
        setMessages(MOCK_MESSAGES[conv.conversation_id] || []);
      }
    } catch {
      setMessages(MOCK_MESSAGES[conv.conversation_id] || []);
    }
    setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
  };

  const handleSend = async () => {
    if (!newMsg.trim() || !selectedConv) return;
    const msgText = newMsg;
    setNewMsg('');
    const tempMsg = { message_id: `temp_${Date.now()}`, sender_id: user?.user_id || 'me', text: msgText, created_at: new Date().toISOString() };
    setMessages(prev => [...prev, tempMsg]);
    try {
      if (useApi) await api.sendMessage(selectedConv.conversation_id, msgText);
    } catch {}
    setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
  };

  const timeAgo = (d) => {
    if (!d) return '';
    const h = Math.floor((Date.now() - new Date(d).getTime()) / 3600000);
    if (h < 1) return 'Just now';
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
  };

  const filteredConvs = conversations.filter(c => c.company_name?.toLowerCase().includes(searchQuery.toLowerCase()));

  const isSentByMe = (msg) => {
    if (user?.user_id) return msg.sender_id === user.user_id;
    return msg.sender_id === 'me' || msg.sender_id === '__seed__';
  };

  return (
    <div className="flex min-h-screen bg-[#F9FBF5]">
      <Sidebar />
      <div className="flex-1 ml-0 md:ml-[70px] pt-[60px] md:pt-0 pb-[60px] md:pb-0 flex flex-col md:flex-row h-screen">
        {/* Conversation List */}
        <div className={`${selectedConv ? 'hidden md:flex' : 'flex'} w-full md:w-80 bg-white border-r border-gray-200 flex-col`}>
          <div className="p-4 border-b">
            <h2 className="text-xl font-bold text-gray-900 mb-3">Messages</h2>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input className="pl-9" placeholder="Search messages..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {filteredConvs.map((conv) => (
              <button key={conv.conversation_id} onClick={() => selectConversation(conv)}
                className={`w-full p-4 flex items-start gap-3 hover:bg-gray-50 transition-colors border-b border-gray-100 text-left ${selectedConv?.conversation_id === conv.conversation_id ? 'bg-[#F1F5E6]' : ''}`}>
                <OptimizedImage src={conv.company_logo} alt="" className="w-10 h-10 rounded-full flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold text-sm text-gray-900 truncate">{conv.company_name}</h4>
                    {conv.unread_count > 0 && <span className="w-5 h-5 bg-[#0A3900] text-white text-xs rounded-full flex items-center justify-center">{conv.unread_count}</span>}
                  </div>
                  <p className="text-sm text-gray-500 truncate">{conv.last_message}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{timeAgo(conv.last_message_at)}</p>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Chat Area */}
        <div className="flex-1 flex flex-col">
          {selectedConv ? (
            <>
              <div className="p-4 border-b bg-white flex items-center gap-3">
                <OptimizedImage src={selectedConv.company_logo} alt="" className="w-10 h-10 rounded-full" />
                <div>
                  <h3 className="font-semibold text-gray-900">{selectedConv.company_name}</h3>
                  <p className="text-sm text-gray-500">Usually responds within an hour</p>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-6 space-y-4">
                {messages.map((msg) => (
                  <div key={msg.message_id} className={`flex ${isSentByMe(msg) ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-md px-4 py-3 rounded-2xl ${isSentByMe(msg) ? 'bg-[#0A3900] text-white' : 'bg-gray-100 text-gray-900'}`}>
                      <p className="text-sm">{msg.text}</p>
                      <p className={`text-xs mt-1 ${isSentByMe(msg) ? 'text-[#B8C4AD]' : 'text-gray-500'}`}>{new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>
              <div className="p-4 border-t bg-white flex gap-3">
                <Button variant="ghost" size="sm"><Paperclip className="w-5 h-5 text-gray-400" /></Button>
                <Input className="flex-1" placeholder="Type a message..." value={newMsg} onChange={(e) => setNewMsg(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSend()} />
                <Button onClick={handleSend} className="bg-[#0A3900] hover:bg-[#1A5C0A]"><Send className="w-4 h-4" /></Button>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-gray-400">
              <div className="text-center">
                <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Send className="w-8 h-8 text-gray-300" />
                </div>
                <p className="text-lg font-medium">Select a conversation</p>
                <p className="text-sm">Choose from the list to start messaging</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MessagesPage;
